import FreeTextSection from "./FreeTextSection";
import RowTextSection from "./RowTextSection";
import SocialHistorySection from "./SocialHistorySection";
import OptionGroupsSection from "./OptionGroupsSection";
import VitalSignsSection from "./VitalSignsSection";
import MedicationSection from "./MedicationSection";
import AllergySection from "./AllergySection";
import MedicalHistorySection from "./MedicalHistorySection";
import SurgicalHistorySection from "./SurgicalHistorySection";
import FamilyHistorySection from "./FamilyHistorySection";
import {
  HOSPITALIZATION_EXCLUSIVE,
  HOSPITALIZATION_ROWS,
  LAST_PCP_ROWS,
  CHIEF_COMPLAINT_PLACEHOLDER,
  SPECIALIST_OPTIONS,
  SPECIALIST_PLACEHOLDER,
} from "../readmission-f2f-constant";
import { REVIEW_OF_SYSTEMS_GROUPS } from "../readmission-f2f-ros-constant";
import {
  ReadmissionF2fPayload,
  SectionDef,
  UpdateSectionFreeText,
} from "../readmission-f2f-types";

export interface SectionRendererProps {
  section: SectionDef;
  expanded: boolean;
  onToggle: () => void;
  payload: ReadmissionF2fPayload;
  update: (patch: Partial<ReadmissionF2fPayload>) => void;
  updateSectionFreeText: UpdateSectionFreeText;
}

const SectionRenderer = ({
  section,
  expanded,
  onToggle,
  payload,
  update,
  updateSectionFreeText,
}: SectionRendererProps) => {
  const { key, title } = section;
  const base = { title, expanded, onToggle };

  const freeTextFallback = (placeholder?: string, rows?: number) => (
    <FreeTextSection
      {...base}
      value={payload.sectionFreeText[key] ?? ""}
      onChange={(value) => updateSectionFreeText(key, value)}
      placeholder={placeholder}
      rows={rows}
    />
  );

  switch (key) {
    case "chiefComplaints":
      return (
        <FreeTextSection
          {...base}
          value={payload.chiefComplaints}
          onChange={(value) => update({ chiefComplaints: value })}
          placeholder={CHIEF_COMPLAINT_PLACEHOLDER}
        />
      );
    case "hpi":
      return (
        <FreeTextSection
          {...base}
          value={payload.historyOfPresentIllness}
          onChange={(value) => update({ historyOfPresentIllness: value })}
          placeholder="Narrative..."
        />
      );
    case "hospitalization":
      return (
        <RowTextSection
          {...base}
          rows={HOSPITALIZATION_ROWS}
          value={payload.hospitalization}
          onChange={(hospitalization) => update({ hospitalization })}
          exclusive={HOSPITALIZATION_EXCLUSIVE}
          freeText={payload.hospitalizationFreeText}
          onFreeTextChange={(hospitalizationFreeText) =>
            update({ hospitalizationFreeText })
          }
          hint="Anything the options above do not cover. Each line is its own entry."
        />
      );
    case "medicalHistory":
      return (
        <MedicalHistorySection
          {...base}
          freeNoteValue={payload.sectionFreeText.medicalHistory ?? ""}
          onFreeNoteChange={(value) =>
            updateSectionFreeText("medicalHistory", value)
          }
        />
      );
    case "surgicalHistory":
      return (
        <SurgicalHistorySection
          {...base}
          freeNoteValue={payload.sectionFreeText.surgicalHistory ?? ""}
          onFreeNoteChange={(value) =>
            updateSectionFreeText("surgicalHistory", value)
          }
        />
      );
    case "familyHistory":
      return (
        <FamilyHistorySection
          {...base}
          freeNoteValue={payload.sectionFreeText.familyHistory ?? ""}
          onFreeNoteChange={(value) =>
            updateSectionFreeText("familyHistory", value)
          }
        />
      );
    case "socialHistory":
      return <SocialHistorySection {...base} />;
    case "allergy":
      return (
        <AllergySection
          {...base}
          freeNoteValue={payload.allergyFreeText}
          onFreeNoteChange={(allergyFreeText) => update({ allergyFreeText })}
        />
      );
    case "medication":
      return <MedicationSection {...base} payload={payload} update={update} />;
    case "lastPcp":
      return (
        <RowTextSection
          {...base}
          rows={LAST_PCP_ROWS}
          value={payload.lastPcpVisitAndLabs}
          onChange={(lastPcpVisitAndLabs) => update({ lastPcpVisitAndLabs })}
          freeText={payload.lastPcpVisitFreeText}
          onFreeTextChange={(lastPcpVisitFreeText) =>
            update({ lastPcpVisitFreeText })
          }
        />
      );
    case "specialist":
      return (
        <RowTextSection
          {...base}
          rows={SPECIALIST_OPTIONS.map((option) => ({
            key: option,
            placeholder: SPECIALIST_PLACEHOLDER,
          }))}
          value={payload.currentMedicalSpecialist}
          onChange={(currentMedicalSpecialist) =>
            update({ currentMedicalSpecialist })
          }
          columns={2}
          freeText={payload.currentMedicalSpecialistFreeText}
          onFreeTextChange={(currentMedicalSpecialistFreeText) =>
            update({ currentMedicalSpecialistFreeText })
          }
          hint="Anything else, for example the patient has their own PCP."
          showCurrentVisit={false}
        />
      );
    case "reviewOfSystems":
      return (
        <OptionGroupsSection
          {...base}
          groups={REVIEW_OF_SYSTEMS_GROUPS}
          store={payload.reviewOfSystems}
          onChange={(reviewOfSystems) => update({ reviewOfSystems })}
          hint="Clickable checklist. Tick only what applies; systems left blank are not printed."
        />
      );
    case "vitalSigns":
      return <VitalSignsSection {...base} payload={payload} update={update} />;
    default:
      return freeTextFallback();
  }
};

export default SectionRenderer;
